import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  brokerSelfSignup,
  PERMISSAO_AUTOCADASTRO_APROVAR,
  PERMISSAO_AUTOCADASTRO_VER,
  PERMISSAO_DADOS_BANCARIOS,
  PERMISSOES_DA_APROVACAO_DO_AUTOCADASTRO,
  type BrokerSelfSignup,
  type BrokerSelfSignupApproval,
  type BrokerSelfSignupStatus,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime, formatDocument } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { CopyButton } from '@/shared/ui/CopyButton';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

/** O que cada permissao da cadeia da aprovacao deixa fazer, para o aviso. */
const rotulosDaCadeia: Record<string, string> = {
  [PERMISSAO_AUTOCADASTRO_APROVAR]: 'aprovar o autocadastro',
  'corretores.corretor.create': 'cadastrar corretor',
  'usuarios.usuario.create': 'convidar usuarios',
  'usuarios.papel.update': 'conceder perfil de acesso',
  [PERMISSAO_DADOS_BANCARIOS]: 'alterar dados bancarios (o pedido traz conta bancaria)',
};

/**
 * As permissoes da cadeia da aprovacao que faltam a sessao. A aprovacao cria a corretora
 * e convida as pessoas, e o servidor confere cada passo: sem a cadeia inteira o botao
 * so levaria a uma recusa (R5-M13).
 */
function faltamParaAprovar(
  pedido: BrokerSelfSignup,
  can: (permissao: string) => boolean,
): string[] {
  const conta = pedido.draft?.broker?.bank_account;
  const temConta = Boolean(conta && (conta.bank_code || conta.agency || conta.account));
  const cadeia: string[] = [...PERMISSOES_DA_APROVACAO_DO_AUTOCADASTRO];
  if (temConta) cadeia.push(PERMISSAO_DADOS_BANCARIOS);
  return cadeia.filter((permissao) => !can(permissao));
}

const rotulosDoAutocadastro: Record<BrokerSelfSignupStatus, string> = {
  pendente_confirmacao: 'Aguardando a confirmacao do e-mail',
  em_preenchimento: 'Em preenchimento',
  em_analise: 'Em analise',
  aprovado: 'Aprovado',
  reprovado: 'Reprovado',
  cancelado: 'Cancelado',
};

function Linha({
  rotulo,
  valor,
}: {
  rotulo: string;
  valor: string | null | undefined;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {rotulo}
      </dt>
      <dd className="text-sm text-ink-900">{valor ? valor : '-'}</dd>
    </div>
  );
}

/** O rascunho enviado, como a seguradora o confere antes de decidir. */
function DadosDoPedido({ pedido }: { pedido: BrokerSelfSignup }) {
  const rascunho = pedido.draft;
  const corretora = rascunho?.broker;
  if (!rascunho || !corretora) {
    return (
      <p className="text-sm text-ink-500">
        Os dados do cadastro foram apagados na decisao (minimizacao). Ficam o CNPJ, a
        razao social, o e-mail de contato e a decisao.
      </p>
    );
  }
  const conta = corretora.bank_account;
  const responsavel = corretora.financial_contact;
  return (
    <div className="flex flex-col gap-4">
      <dl className="grid gap-3 sm:grid-cols-2">
        <Linha rotulo="Razao social" valor={corretora.legal_name} />
        <Linha rotulo="Nome fantasia" valor={corretora.trade_name} />
        <Linha rotulo="No Susep" valor={corretora.susep_code} />
        <Linha rotulo="Inscricao municipal" valor={corretora.municipal_registration} />
        <Linha rotulo="E-mail da corretora" valor={corretora.email} />
        <Linha rotulo="Telefone" valor={corretora.phone} />
        <Linha
          rotulo="Conta bancaria"
          valor={
            conta?.bank_code
              ? `Banco ${conta.bank_code}, agencia ${conta.agency}-${conta.agency_digit}, conta ${conta.account}-${conta.account_digit}`
              : null
          }
        />
        <Linha
          rotulo="Responsavel financeiro"
          valor={
            responsavel?.name ? `${responsavel.name} (${responsavel.email})` : null
          }
        />
      </dl>
      <div>
        <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
          Administrador do portal
        </p>
        <p className="text-sm text-ink-900">
          {rascunho.portal_admin
            ? `${rascunho.portal_admin.name} (${rascunho.portal_admin.email})`
            : 'Nao informado'}
        </p>
      </div>
      <div>
        <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
          Usuarios ({rascunho.users?.length ?? 0})
        </p>
        <ul className="text-sm text-ink-900">
          {(rascunho.users ?? []).map((usuario) => (
            <li key={usuario.email}>
              {usuario.name} ({usuario.email})
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * Painel do cartao de autocadastro da corretora na esteira Cadastro (P-ACE-2).
 *
 * O cartao encerra pela decisao do pedido, como o de nomeacao: Aprovar cria a
 * corretora e convida o administrador do portal e os usuarios, pelos mesmos caminhos
 * do assistente da seguradora; Reprovar pede o motivo. Os dois apagam o rascunho.
 */
export function BrokerSelfSignupCardPanel({
  registrationId,
}: {
  registrationId: string;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [nota, setNota] = useState('');
  const [motivo, setMotivo] = useState('');
  const [reprovando, setReprovando] = useState(false);
  const [aprovado, setAprovado] = useState<BrokerSelfSignupApproval | null>(null);

  const podeVer = can(PERMISSAO_AUTOCADASTRO_VER);
  const podeDecidir = can(PERMISSAO_AUTOCADASTRO_APROVAR);

  const consulta = useQuery({
    queryKey: ['broker-registrations', registrationId],
    queryFn: ({ signal }) => brokerSelfSignup.get(registrationId, signal),
    enabled: podeVer && registrationId !== '',
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['broker-registrations'] });
    void queryClient.invalidateQueries({ queryKey: ['pipe-cards'] });
    void queryClient.invalidateQueries({ queryKey: ['pipes'] });
    void queryClient.invalidateQueries({ queryKey: ['brokers'] });
  }

  const aprovar = useMutation({
    mutationFn: () => brokerSelfSignup.approve(registrationId, nota),
    onSuccess: (resultado) => {
      toast.success('Autocadastro aprovado. A corretora foi cadastrada.');
      setAprovado(resultado);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const reprovar = useMutation({
    mutationFn: () => brokerSelfSignup.reject(registrationId, motivo),
    onSuccess: () => {
      toast.success('Autocadastro reprovado.');
      setReprovando(false);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!podeVer) {
    return (
      <Alert tone="info">
        O autocadastro pede a permissao de ver os autocadastros de corretora.
      </Alert>
    );
  }
  if (consulta.isPending) return <SkeletonText lines={6} />;
  if (consulta.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel abrir o autocadastro">
        {consulta.error.message}
      </Alert>
    );
  }

  const pedido = consulta.data;
  const emAnalise = pedido.status === 'em_analise';
  const faltam = faltamParaAprovar(pedido, can);
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          tone={
            emAnalise ? 'warning' : pedido.status === 'aprovado' ? 'success' : 'neutral'
          }
        >
          {rotulosDoAutocadastro[pedido.status]}
        </Badge>
        {pedido.broker_id ? (
          <Link
            to={`/corretores/${pedido.broker_id}`}
            className="text-xs font-medium text-brand-600 hover:underline"
          >
            Abrir o cadastro da corretora
          </Link>
        ) : null}
      </div>

      {pedido.channel === 'cadastro_publico' ? (
        <Alert tone="info" title="Cadastro publico com ativacao por e-mail">
          A corretora passou na triagem da SUSEP e foi cadastrada pelo endereco publico.{' '}
          {pedido.email_confirmed_at
            ? `O administrador do portal (${pedido.contact_email}) ativou a conta pelo link do e-mail em ${formatDateTime(pedido.email_confirmed_at)}, e a corretora que estava na etapa Novo passou para Ativo.`
            : `O administrador do portal (${pedido.contact_email}) so entra depois de clicar no link de ativacao enviado ao e-mail. Com o clique, a corretora passa da etapa Novo para Ativo; sem ele, fica em Novo.`}
        </Alert>
      ) : null}

      <dl className="grid gap-3 sm:grid-cols-2">
        <Linha rotulo="CNPJ" valor={formatDocument(pedido.document)} />
        <Linha rotulo="Razao social" valor={pedido.legal_name} />
        <Linha rotulo="E-mail de contato" valor={pedido.contact_email} />
        <Linha
          rotulo="E-mail confirmado em"
          valor={
            pedido.email_confirmed_at ? formatDateTime(pedido.email_confirmed_at) : null
          }
        />
        <Linha
          rotulo="Enviado em"
          valor={pedido.submitted_at ? formatDateTime(pedido.submitted_at) : null}
        />
        <Linha
          rotulo="Base legal (LGPD)"
          valor={`Art. 7o, V (procedimentos preliminares de contrato). Aviso de privacidade ${pedido.privacy_notice_version}, ciencia em ${formatDateTime(pedido.privacy_acknowledged_at)}.`}
        />
        {pedido.decided_at ? (
          <>
            <Linha
              rotulo="Decidido por"
              valor={`${pedido.decided_label} em ${formatDateTime(pedido.decided_at)}`}
            />
            <Linha rotulo="Observacao da decisao" valor={pedido.decision_note} />
          </>
        ) : null}
      </dl>

      <DadosDoPedido pedido={pedido} />

      {aprovado && aprovado.invitations.length > 0 ? (
        <Alert tone="success" title="Convites emitidos">
          <ul className="flex flex-col gap-1">
            {aprovado.invitations.map((convite) => (
              <li key={convite.id} className="flex flex-wrap items-center gap-2">
                <span>
                  {convite.name} ({convite.email})
                  {convite.delivered_by_email ? ' - enviado por e-mail' : ''}
                </span>
                {convite.delivered_by_email ? null : (
                  <CopyButton
                    value={convite.accept_url}
                    label="Copiar o endereco do convite"
                  />
                )}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {emAnalise && podeDecidir ? (
        <section aria-label="Decisao do autocadastro" className="flex flex-col gap-3">
          {reprovando ? (
            <>
              <Textarea
                label="Motivo da reprovacao"
                required
                value={motivo}
                maxLength={2000}
                onChange={(evento) => setMotivo(evento.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  disabled={motivo.trim() === ''}
                  loading={reprovar.isPending}
                  onClick={() => reprovar.mutate()}
                >
                  Confirmar a reprovacao
                </Button>
                <Button variant="ghost" onClick={() => setReprovando(false)}>
                  Cancelar
                </Button>
              </div>
            </>
          ) : (
            <>
              <Textarea
                label="Observacao da aprovacao"
                value={nota}
                maxLength={2000}
                onChange={(evento) => setNota(evento.target.value)}
              />
              <p className="text-2xs text-ink-500">
                Aprovar cria a corretora na etapa novo da esteira de ativacao e convida
                o administrador do portal e os usuarios informados.
              </p>
              {faltam.length > 0 ? (
                <Alert tone="info" title="A aprovacao pede outras permissoes">
                  Aprovar cria a corretora e convida as pessoas, e pede tambem a
                  permissao de {faltam.map((p) => rotulosDaCadeia[p] ?? p).join(', ')}.
                  Sem elas, so a reprovacao fica disponivel.
                </Alert>
              ) : null}
              <div className="flex gap-2">
                {faltam.length === 0 ? (
                  <Button loading={aprovar.isPending} onClick={() => aprovar.mutate()}>
                    Aprovar cadastro
                  </Button>
                ) : null}
                <Button variant="secondary" onClick={() => setReprovando(true)}>
                  Reprovar cadastro
                </Button>
              </div>
            </>
          )}
        </section>
      ) : null}
    </div>
  );
}
